// ---------------------------------------------------------------------------
// Digital Notice Board — Designer (the studio page)
// ---------------------------------------------------------------------------
// Orchestrates the whole editing experience: element library (left), live
// screen (centre), inspector (right) and slide timeline (bottom), plus a top
// toolbar and the full-screen projection. It owns the ONE board object and a
// small undo/redo history; every child raises callbacks that funnel through the
// `apply` mutator so history and persistence stay consistent.
//
// The board to edit is chosen on the Notice Boards list page and handed over via
// localStorage (`nb.activeBoardId`) so a browser refresh keeps you on the board.
// ---------------------------------------------------------------------------
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useHistory} from "react-router-dom";
import {Box, Flex, Grid, Spinner, useColorModeValue, useToast} from "@chakra-ui/react";

import Card from "../../../../../components/Card/Card.js";
import DesignCanvas from "../../components/DesignCanvas";
import ElementLibrary from "../../components/ElementLibrary";
import PropertiesPanel from "../../components/PropertiesPanel";
import SlideTimeline from "../../components/SlideTimeline";
import DesignerToolbar from "../../components/DesignerToolbar";
import ProjectionView from "../../components/ProjectionView";
import MarqueeStyle from "../../components/MarqueeStyle";

import {getBoard, saveBoard} from "../../services/NoticeBoardService";
import {
    clamp,
    getPreset,
    makeBoard,
    makeElement,
    makeSlide,
    moveItem,
    uid,
} from "../../utils/noticeBoardUtils";

const ACTIVE_KEY = "nb.activeBoardId";

// Proportionally remap every element when the screen shape/resolution changes so
// a layout survives switching between e.g. 16:9 and 9:16.
function rescaleBoard(board, newCanvas) {
    const kx = newCanvas.width / board.canvas.width;
    const ky = newCanvas.height / board.canvas.height;
    const scaleEl = (el) => {
        const out = {...el, x: el.x * kx, y: el.y * ky, w: el.w * kx, h: el.h * ky};
        ["fontSize", "titleSize", "bodySize"].forEach((k) => {
            if (typeof out[k] === "number") out[k] = Math.round(out[k] * ky);
        });
        ["x", "y", "w", "h"].forEach((k) => (out[k] = Math.round(out[k])));
        return out;
    };
    return {
        ...board,
        canvas: newCanvas,
        slides: board.slides.map((s) => ({...s, elements: s.elements.map(scaleEl)})),
    };
}

export default function NoticeBoardDesigner() {
    const history = useHistory();
    const toast = useToast();
    const panelBg = useColorModeValue("white", "gray.700");
    const stageBg = useColorModeValue("gray.100", "gray.800");

    const [board, setBoard] = useState(null);
    const [past, setPast] = useState([]);
    const [future, setFuture] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [selectedId, setSelectedId] = useState(null);
    const [projecting, setProjecting] = useState(false);
    const [saving, setSaving] = useState(false);
    const lastCommitRef = useRef(0);

    // Load (or create) the active board once.
    useEffect(() => {
        const id = localStorage.getItem(ACTIVE_KEY);
        let loaded = id ? getBoard(id).data : null;
        if (!loaded) {
            loaded = makeBoard("Untitled Board");
            saveBoard(loaded);
            localStorage.setItem(ACTIVE_KEY, loaded.id);
        }
        setBoard(loaded);
    }, []);

    // History-aware mutator. `coalesce` collapses rapid edits (drag, sliders)
    // from the same gesture into a single undo step.
    const apply = useCallback(
        (nextBoard, coalesce = false) => {
            setBoard((cur) => {
                const now = Date.now();
                if (!coalesce || now - lastCommitRef.current > 700) {
                    setPast((p) => [...p.slice(-49), cur]);
                    setFuture([]);
                }
                lastCommitRef.current = now;
                return nextBoard;
            });
        },
        []
    );

    const undo = useCallback(() => {
        setPast((p) => {
            if (!p.length) return p;
            const prev = p[p.length - 1];
            setBoard((cur) => {
                setFuture((f) => [cur, ...f]);
                return prev;
            });
            return p.slice(0, -1);
        });
    }, []);

    const redo = useCallback(() => {
        setFuture((f) => {
            if (!f.length) return f;
            const nextState = f[0];
            setBoard((cur) => {
                setPast((p) => [...p, cur]);
                return nextState;
            });
            return f.slice(1);
        });
    }, []);

    // Keyboard shortcuts for undo/redo/delete.
    useEffect(() => {
        const onKey = (e) => {
            if (projecting) return;
            const meta = e.ctrlKey || e.metaKey;
            if (meta && e.key.toLowerCase() === "z" && !e.shiftKey) {
                e.preventDefault();
                undo();
            } else if (meta && (e.key.toLowerCase() === "y" || (e.key.toLowerCase() === "z" && e.shiftKey))) {
                e.preventDefault();
                redo();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [undo, redo, projecting]);

    const currentSlide = board && board.slides[activeIndex];
    const selectedElement = useMemo(
        () => (currentSlide ? currentSlide.elements.find((e) => e.id === selectedId) : null),
        [currentSlide, selectedId]
    );

    // ---- slide/element transforms ----------------------------------------
    const mapActiveSlide = (fn) => ({
        ...board,
        slides: board.slides.map((s, i) => (i === activeIndex ? fn(s) : s)),
    });

    const updateElement = (id, patch, coalesce = true) => {
        apply(
            mapActiveSlide((s) => ({
                ...s,
                elements: s.elements.map((e) => (e.id === id ? {...e, ...patch} : e)),
            })),
            coalesce
        );
    };

    const addElement = (type, atX, atY) => {
        const el = makeElement(type, board.canvas);
        if (atX != null && atY != null) {
            el.x = clamp(Math.round(atX - el.w / 2), 0, board.canvas.width - el.w);
            el.y = clamp(Math.round(atY - el.h / 2), 0, board.canvas.height - el.h);
        }
        apply(mapActiveSlide((s) => ({...s, elements: [...s.elements, el]})));
        setSelectedId(el.id);
    };

    const deleteElement = (id) => {
        apply(mapActiveSlide((s) => ({...s, elements: s.elements.filter((e) => e.id !== id)})));
        setSelectedId(null);
    };

    const duplicateElement = (id) => {
        const src = currentSlide.elements.find((e) => e.id === id);
        if (!src) return;
        const copy = {...src, id: uid("el"), x: src.x + 24, y: src.y + 24};
        apply(mapActiveSlide((s) => ({...s, elements: [...s.elements, copy]})));
        setSelectedId(copy.id);
    };

    // Layer order = array order (later paints on top).
    const layerElement = (id, dir) => {
        apply(
            mapActiveSlide((s) => {
                const idx = s.elements.findIndex((e) => e.id === id);
                if (idx === -1) return s;
                const els = s.elements.slice();
                const [item] = els.splice(idx, 1);
                if (dir === "front") els.push(item);
                else els.unshift(item);
                return {...s, elements: els};
            })
        );
    };

    const updateSlide = (patch) =>
        apply(mapActiveSlide((s) => ({...s, ...patch})), true);

    const addSlide = () => {
        const s = makeSlide(board.canvas, `Slide ${board.slides.length + 1}`);
        apply({...board, slides: [...board.slides, s]});
        setActiveIndex(board.slides.length);
        setSelectedId(null);
    };

    const duplicateSlide = (i) => {
        const copy = JSON.parse(JSON.stringify(board.slides[i]));
        copy.id = uid("slide");
        copy.name = `${copy.name} copy`;
        copy.elements = copy.elements.map((e) => ({...e, id: uid("el")}));
        const slides = board.slides.slice();
        slides.splice(i + 1, 0, copy);
        apply({...board, slides});
        setActiveIndex(i + 1);
    };

    const deleteSlide = (i) => {
        if (board.slides.length <= 1) return;
        const slides = board.slides.filter((_, idx) => idx !== i);
        apply({...board, slides});
        setActiveIndex((cur) => clamp(cur > i ? cur - 1 : cur, 0, slides.length - 1));
        setSelectedId(null);
    };

    const moveSlide = (from, to) => {
        if (to < 0 || to >= board.slides.length) return;
        apply({...board, slides: moveItem(board.slides, from, to)});
        setActiveIndex(to);
    };

    const changePreset = (presetId) => {
        const preset = getPreset(presetId);
        const newCanvas = {preset: preset.id, width: preset.width, height: preset.height};
        apply(rescaleBoard(board, newCanvas));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            saveBoard(board);
            toast({title: "Board saved", status: "success", duration: 2500, isClosable: true});
        } catch (e) {
            toast({title: "Could not save", description: String(e), status: "error", duration: 4000});
        } finally {
            setSaving(false);
        }
    };

    // Autosave on navigation away.
    useEffect(() => {
        return () => {
            if (board) saveBoard(board);
        };
    }, [board]);

    if (!board) {
        return (
            <Flex justify="center" align="center" py="80px">
                <Spinner size="xl" color="blue.400" thickness="4px" />
            </Flex>
        );
    }

    return (
        <Box>
            <MarqueeStyle />
            <DesignerToolbar
                name={board.name}
                presetId={board.canvas.preset}
                canUndo={past.length > 0}
                canRedo={future.length > 0}
                saving={saving}
                onBack={() => {
                    saveBoard(board);
                    history.push("/admin/notice-board/boards");
                }}
                onNameChange={(name) => apply({...board, name}, true)}
                onPresetChange={changePreset}
                onUndo={undo}
                onRedo={redo}
                onSave={handleSave}
                onProject={() => setProjecting(true)}
            />

            <Grid templateColumns={{base: "1fr", lg: "180px 1fr 300px"}} gap="16px" alignItems="start">
                {/* Left — element library */}
                <Card p="14px" bg={panelBg} display={{base: "none", lg: "block"}}>
                    <ElementLibrary onAdd={(type) => addElement(type)} />
                </Card>

                {/* Centre — the screen */}
                <Box>
                    <Card p="16px" bg={stageBg}>
                        <DesignCanvas
                            slide={currentSlide}
                            canvas={board.canvas}
                            selectedId={selectedId}
                            onSelect={setSelectedId}
                            onChange={(id, patch) => updateElement(id, patch, true)}
                            onDropElement={addElement}
                        />
                    </Card>
                    {/* Mobile element library under the canvas */}
                    <Card p="14px" mt="16px" bg={panelBg} display={{base: "block", lg: "none"}}>
                        <ElementLibrary onAdd={(type) => addElement(type)} />
                    </Card>
                </Box>

                {/* Right — inspector */}
                <Card p="16px" bg={panelBg}>
                    <PropertiesPanel
                        element={selectedElement}
                        slide={currentSlide}
                        onChange={(patch) => updateElement(selectedId, patch, true)}
                        onSlideChange={updateSlide}
                        onDelete={() => deleteElement(selectedId)}
                        onDuplicate={() => duplicateElement(selectedId)}
                        onLayer={(dir) => layerElement(selectedId, dir)}
                    />
                </Card>
            </Grid>

            <Box mt="16px">
                <SlideTimeline
                    slides={board.slides}
                    canvas={board.canvas}
                    activeIndex={activeIndex}
                    onSelect={(i) => {
                        setActiveIndex(i);
                        setSelectedId(null);
                    }}
                    onAdd={addSlide}
                    onDuplicate={duplicateSlide}
                    onDelete={deleteSlide}
                    onMove={moveSlide}
                />
            </Box>

            {projecting && (
                <ProjectionView board={board} startIndex={activeIndex} onClose={() => setProjecting(false)} />
            )}
        </Box>
    );
}
